import { forwardRef, useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import { useTranslation } from "react-i18next";
import "react-datepicker/dist/react-datepicker.css";
import "./injoy-date-picker.css";
import { Icon } from "../../inJoy-components/Icon/Icon";

interface CustomInputProps {
	value?: string;
	onClick?: () => void;
	placeholder?: string;
}

interface CustomHeaderProps {
	date: Date;
	decreaseMonth: () => void;
	increaseMonth: () => void;
	decreaseYear: () => void;
	increaseYear: () => void;
	prevMonthButtonDisabled: boolean;
	nextMonthButtonDisabled: boolean;
	prevYearButtonDisabled: boolean;
	nextYearButtonDisabled: boolean;
	changeMonth: (month: number) => void;
	changeYear: (year: number) => void;
}

type HeaderView = "days" | "months" | "years";

interface InjoyDesktopDatePickerProps {
	date?: Date | null;
	onConfirm: (date: Date) => void;
	showTimeSelect?: boolean;
	placeholder?: string;
	className?: string;
}

const isToday = (dateStr?: string): boolean => {
	if (!dateStr) return false;
	const today = new Date();
	const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
	return dateStr.startsWith(todayStr);
};

const CustomInput = forwardRef<HTMLDivElement, CustomInputProps>(
	({ value, onClick, placeholder }, ref) => (
		<div
			ref={ref}
			onClick={onClick}
			className="flex items-center justify-between w-full h-[44px] px-3 bg-color_background_grey_1 rounded-[4px] cursor-pointer hover:bg-gray-100 transition-colors group"
		>
			<span className="text-sm text-color_text_grey_5">
				{value ? `${value}${isToday(value) ? " (Today)" : ""}` : placeholder}
			</span>
			<div className="text-gray-400 group-hover:text-gray-600">
				<Icon iconName="calendar_outline" size="Small" />
			</div>
		</div>
	),
);

CustomInput.displayName = "CustomInput";

/**
 * Desktop variant of the DatePicker.
 * Uses a popover instead of a bottom sheet.
 * Fixed with forwardRef and portal support for better usability in modals.
 */
export const InjoyDesktopDatePicker = ({
	date,
	onConfirm,
	showTimeSelect = false,
	placeholder = "",
	className = "",
}) => {
	const { t, i18n } = useTranslation();
	const displayPlaceholder = placeholder || t("common.select_date_time");
	const [selectedDate, setSelectedDate] = useState(
		date instanceof Date ? date : new Date(),
	);
	const [headerView, setHeaderView] = useState<HeaderView>("days");

	useEffect(() => {
		if (date instanceof Date) {
			setSelectedDate(date);
		}
	}, [date]);

	const handleChange = (date: Date | null) => {
		if (date) {
			if (headerView === "years") {
				setHeaderView("months");
			} else if (headerView === "months") {
				setHeaderView("days");
			}
			setSelectedDate(date);
			if (headerView === "days") {
				onConfirm(date);
			}
		}
	};

	const months = [
		"Jan",
		"Feb",
		"Mar",
		"Apr",
		"May",
		"Jun",
		"Jul",
		"Aug",
		"Sep",
		"Oct",
		"Nov",
		"Dec",
	];

	// Custom header for the date picker
	const CustomHeader = ({
		date: headerDate,
		decreaseMonth,
		increaseMonth,
		decreaseYear,
		increaseYear,
		prevMonthButtonDisabled,
		nextMonthButtonDisabled,
		prevYearButtonDisabled,
		nextYearButtonDisabled,
		changeMonth,
		changeYear,
	}: CustomHeaderProps) => {
		if (headerView === "years") {
			const currentYear = headerDate.getFullYear();
			const startYear = currentYear - (currentYear % 12);
			const years = Array.from({ length: 12 }, (_, i) => startYear + i);

			return (
				<div>
					<div className="flex justify-between items-center mb-2">
						<button
							onClick={() => changeYear(startYear - 12)}
							className="p-1 hover:bg-gray-100 rounded-full transition-colors"
						>
							<Icon iconName="arrow_left" size="Small" color="#333" />
						</button>
						<div className="text-sm font-semibold text-zinc-950">
							{startYear} – {startYear + 11}
						</div>
						<button
							onClick={() => changeYear(startYear + 12)}
							className="p-1 hover:bg-gray-100 rounded-full transition-colors"
						>
							<Icon iconName="arrow_right" size="Small" color="#333" />
						</button>
					</div>
					<div className="grid grid-cols-3 gap-1">
						{years.map((year) => (
							<button
								key={year}
								onClick={() => {
									changeYear(year);
									setHeaderView("months");
								}}
								className={`py-2 rounded-lg text-sm transition-colors ${
									year === selectedDate.getFullYear()
										? "bg-[#0060e5] text-white font-semibold"
										: "hover:bg-gray-100 text-zinc-700"
								}`}
							>
								{year}
							</button>
						))}
					</div>
				</div>
			);
		}

		if (headerView === "months") {
			return (
				<div>
					<div className="flex justify-between items-center mb-2">
						<button
							onClick={decreaseYear}
							disabled={prevYearButtonDisabled}
							className="p-1 hover:bg-gray-100 rounded-full disabled:opacity-50 transition-colors"
						>
							<Icon iconName="arrow_left" size="Small" color="#333" />
						</button>
						<button
							onClick={() => setHeaderView("years")}
							className="text-sm font-semibold text-zinc-950 transition-colors"
						>
							{headerDate.getFullYear()}
						</button>
						<button
							onClick={increaseYear}
							disabled={nextYearButtonDisabled}
							className="p-1 hover:bg-gray-100 rounded-full disabled:opacity-50 transition-colors"
						>
							<Icon iconName="arrow_right" size="Small" color="#333" />
						</button>
					</div>
					<div className="grid grid-cols-3 gap-1">
						{months.map((month, idx) => (
							<button
								key={month}
								onClick={() => {
									changeMonth(idx);
									setHeaderView("days");
								}}
								className={`p-2 rounded-lg text-sm transition-colors ${
									idx === selectedDate.getMonth() &&
									headerDate.getFullYear() === selectedDate.getFullYear()
										? "bg-[#0060e5] text-white font-semibold"
										: "hover:bg-gray-100 text-zinc-700"
								}`}
							>
								{month}
							</button>
						))}
					</div>
				</div>
			);
		}

		return (
			<div className="flex justify-between items-center">
				<button
					onClick={decreaseMonth}
					disabled={prevMonthButtonDisabled}
					className="p-1 hover:bg-gray-100 rounded-full disabled:opacity-50 transition-colors"
				>
					<Icon iconName="arrow_left" size="Small" color="#333" />
				</button>
				<button
					onClick={() => setHeaderView("months")}
					className="text-sm font-semibold text-zinc-950 transition-colors"
				>
					{headerDate.toLocaleString(i18n.language, {
						month: "long",
						year: "numeric",
					})}
				</button>
				<button
					onClick={increaseMonth}
					disabled={nextMonthButtonDisabled}
					className="p-1 hover:bg-gray-100 rounded-full disabled:opacity-50 transition-colors"
				>
					<Icon iconName="arrow_right" size="Small" color="#333" />
				</button>
			</div>
		);
	};

	return (
		<div className={`injoy-desktop-datepicker w-full ${className}`}>
			<DatePicker
				selected={selectedDate}
				onChange={handleChange}
				showTimeSelect={showTimeSelect}
				timeFormat="HH:mm"
				timeIntervals={15}
				timeCaption={t("common.time")}
				dateFormat={showTimeSelect ? "yyyy-MM-dd HH:mm" : "yyyy-MM-dd"}
				placeholderText={displayPlaceholder}
				renderCustomHeader={CustomHeader}
				customInput={<CustomInput placeholder={displayPlaceholder} />}
				onCalendarOpen={() => setHeaderView("days")}
				// Portal to root to escape overflow-y-auto containers like modals
				portalId="root"
				popperClassName="injoy-datepicker-popper"
				calendarClassName={`!border-none shadow-2xl rounded-lg bg-white overflow-hidden ${headerView !== "days" ? "hide-day-grid" : ""}`}
				dayClassName={() => "transition-colors"}
			/>
		</div>
	);
};
